import {
  addDays,
  daysBetween,
  emojiRow,
  LAUNCH_DATE,
  londonDateString,
  puzzleNumber,
  SPORTS,
  TIER_COLOR,
  tierForTotal,
  type DayResult,
  type Sport,
} from '@geosport/shared';
import { useState } from 'react';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { sportLabel } from '../components/SportIcon';
import { readPractice } from '../lib/practice';
import { Link, navigate } from '../lib/router';
import { useApi } from '../lib/useApi';
import { useRound } from '../store/round';

const MAX_DAYS = 60;

function prettyDate(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function Score({ total }: { total: number }) {
  return (
    <span className="font-bold tabular-nums" style={{ color: TIER_COLOR[tierForTotal(total)] }}>
      {total}
    </span>
  );
}

/** Random rounds by sport, and every previous day since launch (GeoSports "Pro" features, free). */
export function Practice() {
  const [sport, setSport] = useState<Sport | null>(null);
  const today = londonDateString();
  const launch = useRound((s) => s.daily?.launchDate) ?? LAUNCH_DATE;
  const { data } = useApi<{ results: DayResult[] }>('/me/history');
  const practice = readPractice();
  const played = new Map((data?.results ?? []).map((r) => [r.date, r]));
  const dayCount = Math.min(MAX_DAYS, Math.max(0, daysBetween(launch, today)));
  const days = Array.from({ length: dayCount }, (_, i) => addDays(today, -(i + 1)));

  return (
    <Shell title="Practice">
      <section className="mb-6 rounded-2xl bg-ink-2 p-4 ring-1 ring-white/10">
        <h2 className="text-lg font-bold">Random round</h2>
        <p className="text-sm text-white/60">
          Five questions from past rounds. Pick a sport or mix them all. Practice rounds are scored
          but never ranked, and never touch your streak.
        </p>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Sport">
          {([null, ...SPORTS.filter((s) => s !== 'other')] as (Sport | null)[]).map((s) => (
            <button
              key={s ?? 'all'}
              type="button"
              onClick={() => setSport(s)}
              aria-pressed={sport === s}
              className={`min-h-10 rounded-full px-3 text-sm font-semibold ${
                sport === s ? 'bg-accent text-ink' : 'bg-white/10 text-white/80 hover:bg-white/15'
              }`}
            >
              {s ? sportLabel(s) : 'All sports'}
            </button>
          ))}
        </div>
        <Button
          className="mt-4 w-full"
          onClick={() => navigate(`/play/random${sport ? `?sport=${sport}` : ''}`)}
          data-testid="start-random"
        >
          Start random round →
        </Button>
        {practice.random.length > 0 && (
          <ol className="mt-4 divide-y divide-white/5 text-sm">
            {practice.random.slice(0, 5).map((r) => (
              <li key={r.finishedAt} className="flex items-center gap-3 py-2">
                <span className="w-24 text-white/60">
                  {new Date(r.finishedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
                <span className="flex-1 tracking-wide">{emojiRow(r.summary.bases)}</span>
                <Score total={r.summary.total} />
              </li>
            ))}
          </ol>
        )}
      </section>

      <section>
        <h2 className="text-lg font-bold">Previous days</h2>
        <p className="mb-3 text-sm text-white/60">
          Missed a day? Replay it. Your ranked score (if you played it that day) stays as it was.
        </p>
        {days.length === 0 ? (
          <p className="rounded-2xl bg-white/5 p-4 text-sm text-white/60">
            No previous days yet. Come back tomorrow!
          </p>
        ) : (
          <ol className="divide-y divide-white/5 rounded-2xl bg-white/5">
            {days.map((date) => {
              const ranked = played.get(date);
              const replay = practice.archive[date];
              return (
                <li key={date} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <div className="flex-1">
                    <div className="font-semibold">{prettyDate(date)}</div>
                    <div className="text-xs text-white/50">
                      Puzzle #{puzzleNumber(date, launch)}
                      {ranked && (
                        <>
                          {' '}
                          · played <Score total={ranked.total} />
                        </>
                      )}
                      {replay && (
                        <>
                          {' '}
                          · replayed <Score total={replay.summary.total} />
                        </>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/play/archive/${date}`}
                    className="inline-flex min-h-10 items-center rounded-lg bg-white/10 px-3 font-semibold hover:bg-white/15"
                  >
                    {ranked || replay ? 'Replay' : 'Play'}
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </Shell>
  );
}
